import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { axiosInstance } from "../../../utils/client";
import { setPaginationOption } from "../filter/filterSlice";

type ReviewItem = {
  id: number;
  productId: number;
};

type ReviewState = {
  reviews: ReviewItem[];
  isLoading: boolean;
  error: string | false;
  totalReviews: number;
  pageSize: number;
  pageCount: number;
  review: any;
};

const initialState: ReviewState = {
  reviews: [],
  isLoading: false,
  error: false,
  totalReviews: 0,
  pageSize: 10,
  pageCount: 1,
  review: null,
};

const reviewSlice = createSlice({
  name: "review",
  initialState,
  reducers: {
    addReview: (state, action) => {
      state.reviews.push(action.payload);
    },
    setReviews: (state, action) => {
      state.reviews = action.payload.reviews;
      state.totalReviews = action.payload.totalReviews;
      state.pageSize = action.payload.pageSize;
      state.pageCount = action.payload.pageCount;
    },
    setReviewLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setReviewError: (state, action) => {
      state.error = action.payload;
    },
    setReview: (state, action) => {
      state.review = action.payload;
    },
  },
});

export const {
  addReview,
  setReviews,
  setReviewLoading,
  setReviewError,
  setReview,
} = reviewSlice.actions;
export default reviewSlice.reducer;

// ----------------------------------------------------------------------

export function getReviewsFilter(
  filters: any,
  productId?: any,
  userId?: any,
  reviewed?: boolean
) {
  return async (dispatch: any, getState: () => any) => {
    try {
      const state = getState();
      const paginationOption = state.filter.paginationOption;

      dispatch(setReviewLoading(true));

      let req = [];

      // Add query parameters for productId, userId, and reviewed
      if (userId) req.push(`userId=${userId}`);
      if (reviewed !== undefined) req.push(`reviewed=${reviewed}`);
      if (productId) req.push(`productId=${productId}`);

      // Process filters
      if (Array.isArray(filters)) {
        filters.forEach((filter: any) => {
          if (filter?.title === "Product") {
            filter.filters.forEach((item: any) => {
              if (item?.selected) {
                req.push(`productId=${item.value}`);
              }
            });
          }
          if (filter?.title === "Order Product") {
            filter.filters.forEach((item: any) => {
              if (item?.selected) {
                req.push(`orderProductId=${item.value}`);
              }
            });
          }
          if (filter?.title === "User") {
            filter.filters.forEach((item: any) => {
              if (item?.selected) {
                req.push(`userId=${item.value}`);
              }
            });
          }
        });
      }

      // Add pagination parameters (pageSize before pageCount)
      if (paginationOption?.pageSize)
        req.push(`pageSize=${paginationOption.pageSize}`);
      if (paginationOption?.page)
        req.push(`pageCount=${paginationOption.page}`);

      // Join all parameters with '&' to form the complete query string
      const queryString = req.join("&");

      const response = await axiosInstance.get(`/review/filter?${queryString}`);

      if (response.status === 200) {
        const { records, pageNumber, pageSize, totalPages, totalRecords } =
          response.data;

        dispatch(
          setReviews({
            reviews: records,
            totalReviews: totalRecords,
            pageSize,
            pageCount: totalPages,
          })
        );

        dispatch(
          setPaginationOption({
            page: pageNumber,
            pageSize,
            totalPages,
            totalRecords,
          })
        );
      } else {
        throw new Error(response.data.message || "Failed to fetch reviews.");
      }
    } catch (error: any) {
      console.error("Error fetching reviews:", error);
      toast.error(error.message || "Failed to fetch review items.");
      dispatch(
        setReviewError(error.message || "Failed to fetch review items.")
      );
    } finally {
      dispatch(setReviewLoading(false));
    }
  };
}

export function saveReview(
  id: number,
  reviewRequest: {
    stars: number;
    comment: string;
    replyComment?: string;
    reviewerId?: string;
  },
  reply: boolean,
  reviewImages: File[]
) {
  return async (dispatch: any) => {
    try {
      dispatch(setReviewLoading(true));
      let formattedData;
      const formData = new FormData();
      if (reply) {
        formattedData = {
          reviewRequest: {
            stars: reviewRequest.stars,
            comment: reviewRequest.comment,
            replyComment: reviewRequest.replyComment,
            reviewerId: reviewRequest.reviewerId,
          },
        };
      } else {
        formattedData = {
          reviewRequest: {
            stars: reviewRequest.stars,
            comment: reviewRequest.comment,
          },
        };
      }
      formData.append("reviewRequest", JSON.stringify(formattedData.reviewRequest));
      formData.append(
        "reviewImages",
        JSON.stringify(reviewImages.map((file) => JSON.stringify(file)))
      );
      // console.log('id.............................roshani',id);
      // Make the API call
      const response = await axiosInstance.put(
        `/token/review/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Review saved successfully!");
        dispatch(addReview(response.data));
      } else {
        throw new Error(response.data.message || "Failed to save review.");
      }
    } catch (error: any) {
      console.error("Error saving review:", error);
      toast.error(error.message || "Failed to save review.");
      dispatch(setReviewError(error.message || "Failed to save review."));
    } finally {
      dispatch(setReviewLoading(false));
    }
  };
}
export function getReviewDetails(id: number) {
  return async (dispatch: any) => {
    try {
      dispatch(setReviewLoading(true));
      const response = await axiosInstance.get(`/review/${id}`);
      if (response.status === 200) {
        dispatch(setReview(response.data));
      } else {
        throw new Error(response.data.message || "Failed to fetch review.");
      }
    } catch (error: any) {
      console.error("Error fetching review:", error);
      toast.error(error.message || "Failed to fetch review.");
      dispatch(setReviewError(error.message || "Failed to fetch review."));
    } finally {
      dispatch(setReviewLoading(false));
    }
  };
}
